import { useDealerCodesGet } from "@dealer-center/ddc-pages-dealer-codes-fetch";
import { Alert } from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";

interface DuplicateDealerCodeValueWarningProps {
  dealerCodeVType?: string;
  dealerCodeValue?: string;
  currentAccountId: string;
}

// Define a generic interface for dealer code items that works with both API data and tests
interface DealerCodeItem {
  dealerCode: string;
  accountId?: string;
}

export const DuplicateDealerCodeValueWarning = ({
  dealerCodeVType,
  dealerCodeValue,
  currentAccountId
}: DuplicateDealerCodeValueWarningProps) => {
  const { data, isLoading, isError } = useDealerCodesGet(dealerCodeVType);
  const isValueAlreadyUsed = whatAccountsUsingDealerCodeValue(
    currentAccountId,
    data || [],
    dealerCodeValue || "" // Provide empty string fallback when dealerCodeValue is undefined
  );
  if (isLoading)
    return (
      <LoadingIndicator
        actionName="Duplicate Dealer Code Value Warning"
        text="Checking for duplicate dealer code value"
      />
    );
  if (isError)
    return (
      <Alert title="Error fetching dealer code data" type="error">
        Unable to check for duplicate dealer code value
      </Alert>
    );
  if (!isValueAlreadyUsed) return null;
  return (
    <Alert title="Duplicate Dealer Code Value" type="caution">
      Dealer code value is already used by: {isValueAlreadyUsed.join(", ")}
    </Alert>
  );
};

export function whatAccountsUsingDealerCodeValue(
  currentAccountId: string,
  dealerCodeList: DealerCodeItem[], // Use the more generic interface
  currentValue: string
): string[] | null {
  if (!dealerCodeList.length || !currentValue) {
    return null;
  }

  // Filter dealer codes matching the current value and having a defined accountId
  // that's different from the current account ID
  const accountsUsingValue = dealerCodeList
    .filter(
      item =>
        item.dealerCode === currentValue &&
        item.accountId &&
        item.accountId !== currentAccountId
    )
    .map(item => item.accountId as string);

  // Remove duplicates without using Set spread syntax
  const uniqueAccounts: string[] = [];
  accountsUsingValue.forEach(account => {
    if (uniqueAccounts.indexOf(account) === -1) {
      uniqueAccounts.push(account);
    }
  });

  return uniqueAccounts.length ? uniqueAccounts : null;
}
